import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Page from '../../../components/Page';
import Loader from '../../../components/Loader';
import {
  getAllocationHistory,
  AllocationHistoryItem,
  AllocationHistoryResponse,
} from '../../../services/company-credits';
import { toastHandler } from '../../../utils/toastHandler';
import moment from 'moment';
import { useAppSelector } from '../../../store';
import { getCompaniesList } from '../../../services/companies-list';

const AllocationHistory = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AllocationHistoryItem[]>([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [actionType, setActionType] = useState<string>('');
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const isAdmin = userRole === 'admin' || userRole === 'adminTier2' || userRole === 'adminTier3';

  // Fetch companies list for admin
  useEffect(() => {
    if (isAdmin) {
      const fetchCompanies = async () => {
        try {
          setLoadingCompanies(true);
          const response = await getCompaniesList({ limit: 1000 });
          setCompanies(response.data || []);
        } catch (error: any) {
          console.error('Error fetching companies:', error);
          // Only show toast if it's a real error, not just empty response
          if (error?.message) {
            toastHandler(error);
          }
        } finally {
          setLoadingCompanies(false);
        }
      };
      fetchCompanies();
    }
  }, [isAdmin]);

  // Fetch allocation history
  const fetchData = async () => {
    try {
      setLoading(true);
      const response: AllocationHistoryResponse = await getAllocationHistory({
        page: page + 1,
        limit,
        companyId: selectedCompany || undefined,
        actionType: actionType || undefined,
      });
      setData(response.data);
      setTotal(response.total);
    } catch (error: any) {
      console.error('Error fetching allocation history:', error);
      toastHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, limit, selectedCompany, actionType]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getActionTypeColor = (actionType: string) => {
    switch (actionType) {
      case 'allocate':
        return 'success';
      case 'reduce':
        return 'warning';
      case 'expiration':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading && data.length === 0) {
    return <Loader />;
  }

  return (
    <Page title="Allocation History">
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Allocation History
          </Typography>
        </Box>

        {/* Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {isAdmin && (
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={companies.find((c) => c.id === selectedCompany) || null}
              onChange={(event, newValue) => {
                setSelectedCompany(newValue?.id || null);
                setPage(0);
              }}
              loading={loadingCompanies}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Company"
                  placeholder="Select a company or leave empty to see all"
                  variant="outlined"
                />
              )}
              sx={{ minWidth: 300 }}
            />
          )}

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={actionType}
              label="Action Type"
              onChange={(e) => {
                setActionType(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="allocate">Allocate</MenuItem>
              <MenuItem value="reduce">Reduce</MenuItem>
              <MenuItem value="expiration">Expiration</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Card>
          <CardContent>
            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    {isAdmin && <TableCell>Company</TableCell>}
                    <TableCell>Agent</TableCell>
                    <TableCell>Action Type</TableCell>
                    <TableCell>Credits Amount</TableCell>
                    <TableCell>Previous Allocated</TableCell>
                    <TableCell>New Allocated</TableCell>
                    <TableCell>Performed By</TableCell>
                    <TableCell>Performed At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No allocation history found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((item) => (
                      <TableRow key={item.id}>
                        {isAdmin && (
                          <TableCell>
                            {item.companyName || 'N/A'}
                            {item.companyEmail && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {item.companyEmail}
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell>{item.agentName || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.actionType}
                            color={getActionTypeColor(item.actionType) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{item.creditsAmount}</TableCell>
                        <TableCell>{item.previousAllocated ?? 'N/A'}</TableCell>
                        <TableCell>{item.newAllocated ?? 'N/A'}</TableCell>
                        <TableCell>{item.performedByName || 'System'}</TableCell>
                        <TableCell>
                          {moment(item.performedAt).format('YYYY-MM-DD HH:mm')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={limit}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};

export default AllocationHistory;

